import { useState, useTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui-kit/accordion';
import { Button } from '@/components/ui-kit/button';
import { Switch } from '@/components/ui-kit/switch';
import { Label } from '@/components/ui-kit/label';
import { Wrench } from 'lucide-react';

import {
  useGetAgentById,
  useUpdateAgentAIConfigurations,
} from '@/modules/gpt-chats/hooks/use-agents';
import { showErrorToast, showSuccessToast } from '@/hooks/use-toast';
import { ErrorTransformer } from '@/modules/gpt-chats/utils/error-transform';
import { IUpdateAgentAIConfigurationPayload } from '@/modules/gpt-chats/types/agent.service.type';
import { AgentConfigAddToolModal } from './agents-config-add-tool-modal';
import { AgentConfigToolCard } from './agents-config-tool-card';

interface ToolsSectionProps {
  agentId: string;
}

export const ToolsSection = ({ agentId }: ToolsSectionProps) => {
  const { t } = useTranslation();
  const { data } = useGetAgentById(agentId);
  const { mutateAsync } = useUpdateAgentAIConfigurations();

  const [isAddToolModalOpen, setIsAddToolModalOpen] = useState(false);
  const [enableTools, setEnableTools] = useState(
    data?.agent?.enable_tools !== undefined ? !!data.agent.enable_tools : false
  );
  const [isPending, startTransition] = useTransition();

  const handleToggleTools = (val: boolean) => {
    startTransition(() => {
      setEnableTools(val);
    });

    if (!data?.agent) return;

    const updatePayload = {
      agent_id: agentId,
      name: data.agent.name,
      description: data.agent.description,
      rag_config: data.agent.rag_config,
      llm_config: data.agent.llm_config,
      memory_config: data.agent.memory_config,
      guardrail: data.agent.guardrail,
      enable_tools: val,
    };

    mutateAsync(updatePayload as unknown as IUpdateAgentAIConfigurationPayload)
      .then((res) => {
        if (!res.is_success) {
          startTransition(() => {
            setEnableTools(!val);
          });
          showErrorToast({ errors: ErrorTransformer(res.detail) });
        } else {
          showSuccessToast({
            description: t('TOOLS_STATUS_UPDATED_SUCCESSFULLY', {
              status: val ? t('ENABLED') : t('DISABLED'),
            }),
          });
        }
      })
      .catch((error) => {
        startTransition(() => {
          setEnableTools(!val);
        });
        showErrorToast({ errors: ErrorTransformer(error) });
      });
  };

  return (
    <>
      <AccordionItem value="tools">
        <div className="relative">
          <AccordionTrigger className="px-5 text-lg hover:no-underline">
            <div className="flex w-full items-center justify-between pr-4">
              <Label className="text-lg">{t('TOOLS')}</Label>
            </div>
          </AccordionTrigger>

          <div className="absolute right-12 top-1/2 z-10 flex -translate-y-1/2 items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setIsAddToolModalOpen(true);
              }}
              className="gap-2"
               disabled={!enableTools || isPending}
            >
              <span className="text-lg">+</span>
              <span>{t('ADD')}</span>
            </Button>

            <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
              <Switch
                id="tools-toggle"
                checked={enableTools}
                onCheckedChange={handleToggleTools}
                disabled={isPending}
                // size="sm"
              />
            </div>
          </div>
        </div>

        <AccordionContent className="grid gap-4 px-5">
          {data?.agent?.tool_ids && data.agent.tool_ids.length > 0 ? (
            data.agent.tool_ids.map((toolId: string) => (
              <AgentConfigToolCard key={toolId} toolId={toolId} agentId={agentId} />
            ))
          ) : (
            <div className="flex min-h-[100px] flex-col items-center justify-center text-center">
              <Wrench className="mb-2 h-8 w-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {t('NO_TOOLS_ADDED_YET')}
              </p>
            </div>
          )}
        </AccordionContent>
      </AccordionItem>

      <AgentConfigAddToolModal
        open={isAddToolModalOpen}
        onOpenChange={setIsAddToolModalOpen}
        agentId={agentId}
      />
    </>
  );
};
